import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  FileText, Plus, Search, Filter, Download, ChevronLeft, ChevronRight,
  MoreVertical, Eye, Edit, Trash2, CheckCircle, XCircle, AlertTriangle,
  Package, Database, DollarSign, CreditCard, Loader2, Shield,
  AlertCircle, Clock, Check, Minus, Upload
} from 'lucide-react'
import { cn, formatCurrency, formatNumber, formatDate } from '../../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { purchasesApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { Pagination } from '../../components/ui/Pagination'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

const statusConfig = {
  draft: { label: 'پیش‌نویس', variant: 'neutral' as const, icon: FileText },
  pending: { label: 'در انتظار پرداخت', variant: 'warning' as const, icon: Clock },
  partial: { label: 'پرداخت جزئی', variant: 'info' as const, icon: CreditCard },
  paid: { label: 'پرداخت شده', variant: 'success' as const, icon: CheckCircle },
  overdue: { label: 'معوق', variant: 'danger' as const, icon: AlertCircle },
  cancelled: { label: 'لغو شده', variant: 'danger' as const, icon: XCircle },
}

export default function BillsPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<BillListParams>({
    page: 1,
    page_size: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
  })
  const [deletedId, setDeletedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.bills(filters),
    queryFn: () => purchasesApi.listBills(filters),
    placeholderData: (prev) => prev,
  })

  const createMutation = useMutation({
    mutationFn: (data: any) => purchasesApi.createBill(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bills() })
      toast.success('بیل ایجاد شد')
      closeModal('create-bill')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'ایجاد ناموفق بود'),
  })

  const payMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => purchasesApi.payBill(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bills() })
      toast.success('پرداخت ثبت شد')
      closeModal('pay-bill')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'پرداخت ناموفق بود'),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.cancelBill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bills() })
      toast.success('بیل لغو شد')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'لغو ناموفق بود'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => purchasesApi.deleteBill(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bills() })
      toast.success('بیل حذف شد')
      closeModal('delete-confirm')
    },
    onError: (err: any) => toast.error(err.response?.data?.detail || 'حذف ناموفق بود'),
  })

  const handlePageChange = (page: number) => setFilters(prev => ({ ...prev, page }))
  const handlePageSizeChange = (size: number) => setFilters(prev => ({ ...prev, page_size: size, page: 1 }))
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setFilters(prev => ({ ...prev, page: 1 })) }

  const totalPages = data?.total_pages || 1
  const totalItems = data?.total || 0

  const getRowDropdownItems = (bill: any) => {
    const items = [
      { label: 'مشاهده', icon: Eye, onClick: () => window.open(`/purchase/bills/${bill.id}`, '_blank') },
      { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('bill', 'update') || bill.status !== 'draft' },
      { type: 'divider' as const },
    ]

    if (bill.status === 'draft' && hasPermission('bill', 'update')) {
      items.push({ label: 'ثبت پرداخت', icon: CreditCard, onClick: () => openModal('pay-bill') })
      items.push({ label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => { setDeletedId(bill.id); openModal('delete-confirm') } })
    } else if (bill.status === 'pending' && hasPermission('bill', 'update')) {
      items.push({ label: 'ثبت پرداخت', icon: CreditCard, onClick: () => openModal('pay-bill') })
      items.push({ label: 'لغو', icon: XCircle, className: 'text-warning-600', onClick: () => cancelMutation.mutate(bill.id) })
    } else if (bill.status === 'partial' && hasPermission('bill', 'update')) {
      items.push({ label: 'ثبت پرداخت', icon: CreditCard, onClick: () => openModal('pay-bill') })
    } else if (bill.status === 'overdue' && hasPermission('bill', 'update')) {
      items.push({ label: 'ثبت پرداخت', icon: CreditCard, onClick: () => openModal('pay-bill') })
    }

    return items
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">بیل‌ها و پرداخت‌ها</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            {totalItems.toLocaleString('fa-IR')} بیل ثبت شده
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => openModal('create-bill')} disabled={!hasPermission('bill', 'create')}>
            <Plus className="w-4 h-4" />
            بیل جدید
          </Button>
        </div>
      </motion.div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 md:grid-cols-5 gap-4"
      >
        {[
          { label: 'پیش‌نویس', count: data?.items.filter((b: any) => b.status === 'draft').length || 0, color: 'neutral', icon: FileText },
          { label: 'در انتظار', count: data?.items.filter((b: any) => b.status === 'pending').length || 0, color: 'warning', icon: Clock },
          { label: 'پرداخت جزئی', count: data?.items.filter((b: any) => b.status === 'partial').length || 0, color: 'info', icon: CreditCard },
          { label: 'پرداخت شده', count: data?.items.filter((b: any) => b.status === 'paid').length || 0, color: 'success', icon: CheckCircle },
          { label: 'معوق', count: data?.items.filter((b: any) => b.status === 'overdue').length || 0, color: 'danger', icon: AlertCircle },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            className="card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{stat.label}</p>
                <p className="text-2xl font-bold text-neutral-900 dark:text-white font-mono">{formatNumber(stat.count)}</p>
              </div>
              <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', `bg-${stat.color}-100 dark:bg-${stat.color}-900/30`)}>
                <stat.icon className={cn('w-5 h-5', `text-${stat.color}-600 dark:text-${stat.color}-400`)} />
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-4"
      >
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <Input placeholder="جستجو (شماره بیل، تامین‌کننده، مرجع)..." className="flex-1 max-w-md" />
          <Select
            value={filters.status || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, status: v || undefined, page: 1 }))}
            options={[
              { value: '', label: 'همه وضعیت‌ها' },
              { value: 'draft', label: 'پیش‌نویس' },
              { value: 'pending', label: 'در انتظار پرداخت' },
              { value: 'partial', label: 'پرداخت جزئی' },
              { value: 'paid', label: 'پرداخت شده' },
              { value: 'overdue', label: 'معوق' },
              { value: 'cancelled', label: 'لغو شده' },
            ]}
            placeholder="وضعیت"
            className="w-36"
          />
          <Select
            value={filters.supplier_id || ''}
            onChange={(v) => setFilters(prev => ({ ...prev, supplier_id: v || undefined, page: 1 }))}
            options={[{ value: '', label: 'همه تامین‌کنندگان' }]}
            placeholder="تامین‌کننده"
            className="w-40"
          />
          <Dropdown
            trigger={<Button variant="outline"><Filter className="w-4 h-4 ml-1" /> فیلترها</Button>}
            items={[
              { label: 'خروجی اکسل', icon: Download, onClick: () => {} },
              { type: 'divider' },
              { label: 'بیل‌های معوق', icon: AlertCircle, onClick: () => {} },
              { label: 'نیاز به پرداخت', icon: Clock, onClick: () => {} },
            ]}
          />
        </form>
      </motion.div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card overflow-hidden"
      >
        {isLoading ? (
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره بیل</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">تامین‌کننده</th>
                    <th className="hidden lg:table-cell">سفارش خرید</th>
                    <th>مبلغ کل</th>
                    <th>پرداخت شده</th>
                    <th>مانده</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td><Skeleton variant="text" width="120px" /></td>
                      <td className="hidden sm:table-cell"><Skeleton variant="text" width="80px" /></td>
                      <td className="hidden md:table-cell"><Skeleton variant="text" width="120px" /></td>
                      <td className="hidden lg:table-cell"><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="text" width="100px" /></td>
                      <td><Skeleton variant="rectangular" width="100px" height="24px" /></td>
                      <td><Skeleton variant="rectangular" width="32px" height="32px" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <p className="text-danger-600 dark:text-danger-400">خطا در بارگذاری بیل‌ها</p>
            <Button variant="outline" onClick={() => refetch()} className="mt-4">تلاش مجدد</Button>
          </div>
        ) : data?.items.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">هیچ بیل‌ای یافت نشد</h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-4">اولین بیل را ایجاد کنید</p>
            <Button onClick={() => openModal('create-bill')} disabled={!hasPermission('bill', 'create')}>
              <Plus className="w-4 h-4 ml-1" />
              ایجاد بیل
            </Button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>شماره بیل</th>
                    <th className="hidden sm:table-cell">تاریخ</th>
                    <th className="hidden md:table-cell">تامین‌کننده</th>
                    <th className="hidden lg:table-cell">سفارش خرید</th>
                    <th>مبلغ کل</th>
                    <th>پرداخت شده</th>
                    <th>مانده</th>
                    <th>وضعیت</th>
                    <th className="w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {data!.items.map((bill, index) => {
                    const statusInfo = statusConfig[bill.status as keyof typeof statusConfig] || statusConfig.draft
                    const StatusIcon = statusInfo.icon
                    const balance = bill.total_amount - bill.paid_amount
                    return (
                      <motion.tr
                        key={bill.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className={bill.status === 'overdue' ? 'bg-danger-50/50 dark:bg-danger-900/20' : ''}
                      >
                        <td className="font-medium text-neutral-900 dark:text-white font-mono">{bill.bill_number}</td>
                        <td className="hidden sm:table-cell text-neutral-600 dark:text-neutral-400 text-sm">{formatDate(bill.bill_date)}</td>
                        <td className="hidden md:table-cell text-neutral-700 dark:text-neutral-300">{bill.supplier?.name || '—'}</td>
                        <td className="hidden lg:table-cell text-neutral-700 dark:text-neutral-300 font-mono">{bill.purchase_order?.order_number || '—'}</td>
                        <td className="font-mono text-sm text-neutral-900 dark:text-white">{formatCurrency(bill.total_amount)}</td>
                        <td className="font-mono text-sm text-success-600 dark:text-success-400">{formatCurrency(bill.paid_amount)}</td>
                        <td className="font-mono text-sm {balance > 0 ? 'text-danger-600 dark:text-danger-400' : 'text-success-600 dark:text-success-400'}">{formatCurrency(balance)}</td>
                        <td>
                          <Badge variant={statusInfo.variant} className="text-xs flex items-center gap-1">
                            <StatusIcon className="w-3 h-3" />
                            {statusInfo.label}
                          </Badge>
                        </td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={getRowDropdownItems(bill)}
                          />
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                نمایش {((filters.page - 1) * filters.page_size) + 1} تا {Math.min(filters.page * filters.page_size, totalItems)} از {totalItems.toLocaleString('fa-IR')}
              </div>
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                showPageSize
                pageSize={filters.page_size}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </motion.div>

      {/* Create Bill Modal */}
      <Modal
        isOpen={modals['create-bill']}
        onClose={() => closeModal('create-bill')}
        title="ایجاد بیل جدید"
        size="lg"
      >
        <BillForm onSubmit={createMutation.mutate} onClose={() => closeModal('create-bill')} isLoading={createMutation.isPending} />
      </Modal>

      {/* Pay Bill Modal */}
      <Modal
        isOpen={modals['pay-bill']}
        onClose={() => closeModal('pay-bill')}
        title="ثبت پرداخت"
        size="md"
      >
        <PayBillForm onSubmit={payMutation.mutate} onClose={() => closeModal('pay-bill')} isLoading={payMutation.isPending} />
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={modals['delete-confirm']}
        onClose={() => { closeModal('delete-confirm'); setDeletedId(null) }}
        title="حذف بیل"
        size="sm"
      >
        <p className="text-neutral-600 dark:text-neutral-400">
          آیا از حذف این بیل مطمئن هستید؟
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => { closeModal('delete-confirm'); setDeletedId(null) }}>انصراف</Button>
          <Button variant="danger" onClick={() => deletedId && deleteMutation.mutate(deletedId)} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال حذف... </> : 'حذف' }
          </Button>
        </div>
      </Modal>
    </div>
  )
}

const billSchema = z.object({
  supplier_id: z.string().uuid('تأمین‌کننده معتبر نیست'),
  purchase_order_id: z.string().uuid().optional().nullable(),
  bill_date: z.string().min(1, 'تاریخ بیل الزامی است'),
  due_date: z.string().min(1, 'تاریخ سررسید الزامی است'),
  reference_number: z.string().optional(),
  notes: z.string().optional(),
})

type BillFormData = z.infer<typeof billSchema>

interface BillFormProps {
  onSubmit: (data: BillFormData) => void
  onClose: () => void
  isLoading: boolean
}

function BillForm({ onSubmit, onClose, isLoading }: BillFormProps) {
  const methods = useForm<BillFormData>({
    resolver: zodResolver(billSchema),
    defaultValues: {
      supplier_id: '',
      purchase_order_id: null,
      bill_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      reference_number: '',
      notes: '',
    },
  })

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="supplier_id"
          control={methods.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select label="تأمین‌کننده *" options={[{ value: '', label: 'انتخاب کنید' }]} placeholder="تأمین‌کننده" {...field} />
          )}
        />
        <Controller
          name="purchase_order_id"
          control={methods.control}
          render={({ field }) => (
            <Select label="سفارش خرید (اختیاری)" options={[{ value: '', label: 'بدون سفارش خرید' }]} placeholder="سفارش خرید" {...field} />
          )}
        />
        <Controller
          name="bill_date"
          control={methods.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input label="تاریخ بیل *" type="date" {...field} />
          )}
        />
        <Controller
          name="due_date"
          control={methods.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input label="تاریخ سررسید *" type="date" {...field} />
          )}
        />
        <Controller
          name="reference_number"
          control={methods.control}
          render={({ field }) => (
            <Input label="شماره مرجع" placeholder="شماره فاکتور تامین‌کننده" {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="notes"
          control={methods.control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">یادداشت‌ها</label>
              <textarea
                {...field}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 border-neutral-300 dark:border-neutral-700"
                placeholder="یادداشت‌های مربوط به این بیل..."
              />
            </div>
          )}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <DollarSign className="w-4 h-4 ml-1" /> ثبت بیل </> }
        </Button>
      </div>
    </form>
  )
}

const payBillSchema = z.object({
  amount: z.number().min(1, 'مبلغ باید مثبت باشد'),
  payment_method: z.enum(['cash', 'bank_transfer', 'check', 'card', 'pos', 'wallet']),
  reference: z.string().optional(),
  payment_date: z.string().min(1, 'تاریخ پرداخت الزامی است'),
  notes: z.string().optional(),
})

type PayBillFormData = z.infer<typeof payBillSchema>

interface PayBillFormProps {
  onSubmit: (data: PayBillFormData) => void
  onClose: () => void
  isLoading: boolean
}

function PayBillForm({ onSubmit, onClose, isLoading }: PayBillFormProps) {
  const methods = useForm<PayBillFormData>({
    resolver: zodResolver(payBillSchema),
    defaultValues: {
      amount: 0,
      payment_method: 'bank_transfer',
      reference: '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: '',
    },
  })

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Controller
          name="amount"
          control={methods.control}
          rules={{ required: true, min: 1 }}
          render={({ field }) => (
            <Input label="مبلغ (ریال) *" type="number" min={1} step={10000} {...field} />
          )}
        />
        <Controller
          name="payment_method"
          control={methods.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              label="روش پرداخت *"
              options={[
                { value: 'bank_transfer', label: 'انتقال بانکی' },
                { value: 'cash', label: 'نقدی' },
                { value: 'check', label: 'چک' },
                { value: 'card', label: 'کارت به کارت' },
                { value: 'pos', label: 'پاس' },
                { value: 'wallet', label: 'کیف پول' },
              ]}
              placeholder="روش پرداخت"
              {...field}
            />
          )}
        />
        <Controller
          name="payment_date"
          control={methods.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Input label="تاریخ پرداخت *" type="date" {...field} />
          )}
        />
        <Controller
          name="reference"
          control={methods.control}
          render={({ field }) => (
            <Input label="شماره مرجع/چک" placeholder="شماره پیگیری/شماره چک" {...field} className="md:col-span-2" />
          )}
        />
        <Controller
          name="notes"
          control={methods.control}
          render={({ field }) => (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">یادداشت‌ها</label>
              <textarea
                {...field}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 border-neutral-300 dark:border-neutral-700"
                placeholder="یادداشت‌های پرداخت..."
              />
            </div>
          )}
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-800">
        <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>انصراف</Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? <> <Loader2 className="w-4 h-4 animate-spin" /> در حال ثبت... </> : <> <Check className="w-4 h-4 ml-1" /> ثبت پرداخت </> }
        </Button>
      </div>
    </form>
  )
}

type BillListParams = {
  page: number
  page_size: number
  sort_by: string
  sort_order: 'asc' | 'desc'
  search?: string
  status?: string
  supplier_id?: string
  purchase_order_id?: string
  date_from?: string
  date_to?: string
}